<script setup lang="ts">
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';
import type { TIdNumberType } from '~/interfaces/stores/auth.store.interface';
import { changeIdType, validateIdField } from '~/utilities/form-builder.reg';

const props = withDefaults(
  defineProps<{
    mode?: 'logged-in' | 'logged-out';
    expectedIdNumber?: string | null;
    expectedIdNumberType?: string;
    disabled?: boolean;
    showTitle?: boolean;
  }>(),
  {
    mode: 'logged-in',
    expectedIdNumber: null,
    expectedIdNumberType: null,
    disabled: false,
    showTitle: undefined,
  },
);

const showTitle = computed(() => props.showTitle ?? props.mode === 'logged-in');

const { $t } = useNuxtApp();
const form = ref<IFormBuilderReference>();
const loading = ref<boolean>(true);
const model = ref<{ idNumber: string; idNumberType: TIdNumberType }>({
  idNumber: '',
  idNumberType: 'IdNumber',
});

const emits = defineEmits<{
  (e: 'error', payload: { errorRef: string; errorCode: string }): void;
  (e: 'account-confirmed'): void;
  (
    e: 'id-entered',
    payload: { idNumber: string; idNumberType: TIdNumberType },
  ): void;
}>();

const onSubmit = () => {
  const idNumber = model.value?.idNumber ?? '';
  const idNumberType = model.value?.idNumberType ?? '';
  if (props.mode === 'logged-in') {
    const expectedId = props.expectedIdNumber ?? '';
    const expectedIdNumberType = props.expectedIdNumberType ?? '';
    const isConfirmed =
      idNumber.trim().toLowerCase() === expectedId.trim().toLowerCase() &&
      idNumberType.trim().toLowerCase() ===
        expectedIdNumberType.trim().toLowerCase();
    if (isConfirmed) {
      emits('account-confirmed');
      emits('error', { errorRef: '', errorCode: '' });
    } else {
      emits('error', { errorRef: 'invalid-id', errorCode: 'invalid-id' });
      model.value.idNumber = '';
      model.value.idNumberType = 'IdNumber';
    }
  } else {
    emits('id-entered', {
      idNumber,
      idNumberType: idNumberType as TIdNumberType,
    });
  }
};

watch(
  () => model.value.idNumber,
  () => {
    if (form.value) validateIdField(form as Ref<IFormBuilderReference>);
  },
);

watch(
  () => model.value.idNumberType,
  () => {
    if (form.value) changeIdType(form as Ref<IFormBuilderReference>);
  },
  { immediate: true },
);
</script>

<template>
  <div
    :class="{
      'bg-layer space-y-4 w-full p-4 rounded-xl': mode === 'logged-in',
    }"
  >
    <LazyUserInterfaceGenericLoader v-if="loading" />
    <UserInterfaceDynamicFormBuilder
      ref="form"
      v-model="model"
      template-name="confirm id"
      :show-title="showTitle"
      :title-classes="[{ 'pb-2 font-bold': true }]"
      :form-classes="[
        {
          'py-4 flex flex-col gap-2': mode === 'logged-in',
          'p-4 flex flex-col gap-2': mode === 'logged-out',
        },
      ]"
      @error="emits('error', { errorRef: $event, errorCode: $event })"
      @empty-schema="emits('error', { errorRef: $event, errorCode: $event })"
      @loaded="loading = false"
      @keyup.enter="onSubmit"
    >
      <template v-if="mode === 'logged-out'" #before>
        <p class="text-base-priority text-xs lg:text-sm">
          {{ $t('enter-valid-passport-or-id') }}
        </p>
      </template>
      <template #form-actions="actionProps">
        <slot name="error" />
        <div :class="mode === 'logged-out' ? 'p-4 bg-base' : 'rounded-b-lg'">
          <DesignSystemButton
            class="w-full justify-center"
            rounding="lg"
            :disabled="
              !(actionProps.form?.validated && !actionProps.form?.invalid) ||
              disabled
            "
            @click="onSubmit"
          >
            {{ $t('confirm-account') }}
          </DesignSystemButton>
        </div>
      </template>
    </UserInterfaceDynamicFormBuilder>
  </div>
</template>
